import Cache from '@fs/zion-cache'
import { useTranslation } from 'react-i18next'
import { usePersonPageParams } from '../person-page/personPageRouteUtil'
import { useSwrRequest } from './useSwrRequest'

export type MyHeritageHintDetail = {
  modifier: string
  name: string
  type: string
}

export type MyHeritageHintsResponse = {
  factorsDescription?: string
  /** Optional because a 204 or a partial response from the partner can omit it entirely. */
  hintSummaryDetails?: MyHeritageHintDetail[]
  link: string
  totalHints: number
}

const myHeritageCache = new Cache({ storeName: 'myHeritageHints_r9' })

const hintFieldTranslationKeys = {
  birthplace: 'tree-person.myheritage-hints.birth-place',
  birthdate: 'tree-person.myheritage-hints.birth-date',
  burialplace: 'tree-person.myheritage-hints.burial-place',
  deathplace: 'tree-person.myheritage-hints.death-place',
  deathdate: 'tree-person.myheritage-hints.death-date',
  occupation: 'tree-person.myheritage-hints.occupation',
  residence: 'tree-person.myheritage-hints.residence',
  father: 'tree-person.myheritage-hints.father',
  mother: 'tree-person.myheritage-hints.mother',
  spouse: 'tree-person.myheritage-hints.spouse',
  story: 'tree-person.myheritage-hints.story',
  personalphoto: 'tree-person.myheritage-hints.personal-photo',
  additionalphoto: 'tree-person.myheritage-hints.additional-photo',
  middlename: 'tree-person.myheritage-hints.middle-name',
  education: 'tree-person.myheritage-hints.education',
  sibling: 'tree-person.myheritage-hints.sibling',
  child: 'tree-person.myheritage-hints.child',
}

/**
 * Fetches the MyHeritage hint summary for the focus person, plus the sentence that describes what
 * those matches would add.
 *
 * Extracted from ResearchHintMatchesList because the tab counts need to know whether the person has
 * matches, and those components never render a row. Every caller shares one SWR entry keyed by the
 * URL, so adding a caller does not add a request.
 *
 * Pass `false` to skip the request, the way useTree2TreeMatches skips on a missing personId. The
 * argument is the SWR key switch, not a rendering hint, so every caller must pass whatever gates it:
 * useShowMyHeritageHints at minimum, plus any surface condition. That way the partner service is
 * never asked about a living or restricted person, nor from a page that ignores the answer.
 *
 * `addsText` is the partner's own sentence when it sends one, and otherwise the translated field
 * list behind an "Adds:" prefix. Either form reads on its own, and the partner's sentence already
 * says "Adds:" itself, so callers embed it whole rather than adding a prefix of their own.
 */
export default function useMyHeritageHints(enabled = true): {
  addsText: string
  data: MyHeritageHintsResponse | undefined
  hasMatches: boolean
  isLoading: boolean
} {
  const { pid } = usePersonPageParams('full')
  const [t] = useTranslation()
  const url = enabled ? `/service/external/hints/myheritage/persons/${pid}/hints` : null
  const { data, isLoading } = useSwrRequest<MyHeritageHintsResponse>(url, myHeritageCache, {
    dedupingInterval: 10000, // Four callers mount at staggered times; the default 2s window is too thin
    focusThrottleInterval: 60 * 1000 * 2, // revalidateOnFocus is on in prod, so throttle partner refetches
  })

  const translateField = (hintName: string): string =>
    hintFieldTranslationKeys[hintName] ? t(hintFieldTranslationKeys[hintName]) : hintName

  // The optional chain matters: a 204 or a partial response can omit hintSummaryDetails entirely,
  // and an undefined here would reach the UI as "Adds: undefined".
  const fieldList = data?.hintSummaryDetails?.map((hint) => translateField(hint.name)).join(', ') || ''
  const addsText =
    data?.factorsDescription || (fieldList && t('tree-person.myheritage-matches.adds', { hints: fieldList }))

  return { addsText: addsText || '', data, hasMatches: (data?.totalHints ?? 0) > 0, isLoading }
}
